<template>
  <div class="container max-w-6xl mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold text-center mb-8 text-gray-800">Отслеживание здоровья</h1>
    
    <!-- Tabs for different sections -->
    <div class="flex border-b mb-6">
      <button 
        @click="activeTab = 'tracking'" 
        class="py-2 px-4 font-medium"
        :class="activeTab === 'tracking' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'"
      >
        Мониторинг здоровья
      </button>
      <button 
        @click="activeTab = 'add'" 
        class="py-2 px-4 font-medium"
        :class="activeTab === 'add' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-600'"
      >
        Добавить показатели
      </button>
    </div>
    
    <!-- Health tracking history section -->
    <div v-if="activeTab === 'tracking'" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-800">История показателей здоровья</h2>
        
        <div class="flex space-x-3">
          <select 
            v-model="selectedMetric" 
            class="px-3 py-2 border rounded-md text-sm"
          >
            <option value="all">Все показатели</option>
            <option value="blood_pressure">Давление</option>
            <option value="blood_sugar">Сахар крови</option>
            <option value="weight">Вес</option>
          </select>
          
          <select 
            v-model="timeRange" 
            class="px-3 py-2 border rounded-md text-sm"
          >
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
            <option value="3months">3 месяца</option>
            <option value="year">Год</option>
            <option value="all">Все время</option>
          </select>
        </div>
      </div>
      
      <div v-if="loading" class="flex justify-center p-6">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
      
      <div v-else-if="healthData.length === 0" class="text-center p-10 text-gray-600">
        <p>У вас пока нет записей о показателях здоровья</p>
        <button 
          @click="activeTab = 'add'" 
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Добавить первую запись
        </button>
      </div>
      
      <div v-else>
        <!-- Statistics cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div class="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <div class="text-sm text-gray-500">Всего записей</div>
            <div class="text-2xl font-bold">{{ healthStats.total || 0 }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
            <div class="text-sm text-gray-500">Давление</div>
            <div class="text-2xl font-bold">{{ healthStats.blood_pressure || 0 }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <div class="text-sm text-gray-500">Вес</div>
            <div class="text-2xl font-bold">{{ healthStats.weight || 0 }}</div>
          </div>
          <div class="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <div class="text-sm text-gray-500">Сахар крови</div>
            <div class="text-2xl font-bold">{{ healthStats.blood_sugar || 0 }}</div>
          </div>
        </div>
        
        <!-- Charts would be displayed here -->
        <div class="mb-6 h-64 bg-gray-50 rounded-lg p-4 flex items-center justify-center">
          <p class="text-gray-500">Графики показателей здоровья</p>
        </div>
        
        <!-- Health metrics table -->
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-100 text-gray-700">
                <th class="py-3 px-4 text-left">Дата</th>
                <th class="py-3 px-4 text-left">Показатель</th>
                <th class="py-3 px-4 text-left">Значение</th>
                <th class="py-3 px-4 text-left">Примечание</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in healthData.filter(r => r && r.id)" :key="record.id" class="border-b hover:bg-gray-50">
                <td class="py-3 px-4">{{ formatDate(record.recorded_at) }}</td>
                <td class="py-3 px-4">{{ getMetricName(record.metric_type) }}</td>
                <td class="py-3 px-4">{{ formatMetricValue(record) }}</td>
                <td class="py-3 px-4">{{ record.notes || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <!-- Add new health metrics section -->
    <div v-if="activeTab === 'add'" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold mb-6 text-gray-800">Добавить новые показатели здоровья</h2>
      
      <form @submit.prevent="submitHealthData">
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Тип показателя</label>
          <select 
            v-model="newRecord.metric_type" 
            class="form-select w-full rounded-md border-gray-300"
            required
          >
            <option value="" disabled>Выберите тип показателя</option>
            <option value="blood_pressure">Артериальное давление</option>
            <option value="weight">Вес</option>
            <option value="blood_sugar">Уровень сахара в крови</option>
            <option value="pain_scale">Шкала боли</option>
          </select>
        </div>
        
        <!-- Dynamic fields based on selected metric type -->
        <div v-if="newRecord.metric_type === 'blood_pressure'" class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Систолическое (верхнее)</label>
            <input 
              v-model="newRecord.systolic" 
              type="number" 
              class="form-input w-full rounded-md border-gray-300"
              required
              min="60"
              max="250"
            >
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Диастолическое (нижнее)</label>
            <input 
              v-model="newRecord.diastolic" 
              type="number" 
              class="form-input w-full rounded-md border-gray-300"
              required
              min="40"
              max="150"
            >
          </div>
        </div>
        
        <div v-else-if="newRecord.metric_type === 'weight'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Вес (кг)</label>
          <input 
            v-model="newRecord.value" 
            type="number" 
            class="form-input w-full rounded-md border-gray-300"
            required
            min="1"
            max="300"
            step="0.1"
          >
        </div>
        
        <div v-else-if="newRecord.metric_type === 'blood_sugar'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Уровень сахара (ммоль/л)</label>
          <input 
            v-model="newRecord.value" 
            type="number" 
            class="form-input w-full rounded-md border-gray-300"
            required
            min="1"
            max="30"
            step="0.1"
          >
        </div>
        
        <div v-else-if="newRecord.metric_type === 'pain_scale'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Интенсивность боли (0-10)</label>
          <input 
            v-model="newRecord.value" 
            type="number" 
            class="form-input w-full rounded-md border-gray-300"
            required
            min="0"
            max="10"
            step="1"
          >
          <p class="text-sm text-gray-500 mt-1">0 = нет боли, 10 = невыносимая боль</p>
        </div>
        
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">Примечание (необязательно)</label>
          <textarea 
            v-model="newRecord.notes" 
            class="form-textarea w-full rounded-md border-gray-300"
            rows="3"
            placeholder="Добавьте любые наблюдения или заметки"
          ></textarea>
        </div>
        
        <div v-if="submitError" class="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
          {{ submitError }}
        </div>
        
        <div v-if="submitSuccess" class="mb-4 p-3 bg-green-50 text-green-600 rounded-md">
          Показатели здоровья успешно добавлены!
        </div>
        
        <div class="flex justify-end">
          <button 
            type="submit" 
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            :disabled="submitting"
          >
            <span v-if="submitting">Сохранение...</span>
            <span v-else>Сохранить</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'HealthTracking',
  data() {
    return {
      activeTab: 'tracking',
      loading: false,
      healthData: [],
      healthStats: {
        total: 0,
        blood_pressure: 0,
        blood_sugar: 0,
        weight: 0
      },
      selectedMetric: 'all',
      timeRange: 'month',
      submitting: false,
      submitError: null,
      submitSuccess: false,
      newRecord: this.initNewRecord()
    }
  },
  created() {
    this.fetchHealthData()
  },
  watch: {
    selectedMetric() {
      this.fetchHealthData()
    },
    timeRange() {
      this.fetchHealthData()
    }
  },
  methods: {
    initNewRecord() {
      return {
        metric_type: '',
        value: null,
        systolic: null,
        diastolic: null,
        notes: ''
      }
    },
    async fetchHealthData() {
      this.loading = true;
      this.submitError = null;
      
      try {
        // Use the appropriate query parameters for filtering
        const params = {};
        if (this.selectedMetric !== 'all') {
          params.metric_type = this.selectedMetric;
        }
        if (this.timeRange !== 'all') {
          params.time_range = this.timeRange;
        }
        
        const response = await api.getHealthMetrics(params);
        
        // Ensure we have a valid array of health data
        if (Array.isArray(response.data)) {
          // Filter out any null values and ensure all records have required fields
          this.healthData = response.data
            .filter(record => record && record.id && record.metric_type)
            .map(record => ({
              ...record,
              // Ensure recorded_at has a value
              recorded_at: record.recorded_at || record.created_at || new Date().toISOString()
            }));
        } else {
          this.healthData = [];
        }
        
        // Update statistics
        this.healthStats = response.stats || {
          total: 0,
          blood_pressure: 0,
          blood_sugar: 0,
          weight: 0
        };
      } catch (error) {
        this.healthData = []; // Set to empty array if there's an error
        this.submitError = 'Не удалось загрузить данные здоровья. Пожалуйста, попробуйте позже.';
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      
      // Formatter for nice date and time display with timezone GMT+5
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
      
      // Return a nicely formatted date in Russian
      return date.toLocaleString('ru-RU', options)
    },
    getMetricName(metricType) {
      const metricNames = {
        'blood_pressure': 'Артериальное давление',
        'weight': 'Вес',
        'blood_sugar': 'Уровень сахара в крови'
      }
      return metricNames[metricType] || metricType
    },
    formatMetricValue(record) {
      if (record.metric_type === 'blood_pressure') {
        return `${record.systolic}/${record.diastolic} мм рт.ст.`
      } else if (record.metric_type === 'weight') {
        return `${record.value} кг`
      } else if (record.metric_type === 'blood_sugar') {
        return `${record.value} ммоль/л`
      }
      return record.value
    },
    async submitHealthData() {
      if (!this.newRecord.metric_type) {
        this.submitError = 'Пожалуйста, выберите тип показателя'
        return
      }
      
      this.submitting = true
      this.submitError = null
      this.submitSuccess = false
      
      try {
        // Prepare the data with the structure matching the backend model
        const healthData = {
          metric_type: this.newRecord.metric_type,
          notes: this.newRecord.notes
        }
        
        // Add appropriate values based on metric type
        if (this.newRecord.metric_type === 'blood_pressure') {
          healthData.systolic = this.newRecord.systolic
          healthData.diastolic = this.newRecord.diastolic
        } else {
          healthData.value = this.newRecord.value
        }
        
        // Submit the data
        await api.createHealthMetric(healthData)
        
        // Reset form and show success message
        this.newRecord = this.initNewRecord()
        this.submitSuccess = true
        
        // Refresh the health data
        await this.fetchHealthData()
        
        // After a delay, switch to the tracking tab to show the new data
        setTimeout(() => {
          this.activeTab = 'tracking'
          this.submitSuccess = false
        }, 2000)
      } catch (error) {
        if (error.response && error.response.data) {
          // Display specific error from the API if available
          this.submitError = error.response.data.detail || 'Не удалось сохранить данные. Пожалуйста, попробуйте позже.'
        } else {
          this.submitError = 'Не удалось сохранить данные. Пожалуйста, попробуйте позже.'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.form-input, .form-textarea, .form-select {
  @apply focus:ring-blue-500 focus:border-blue-500;
}
</style> 